import { z } from 'zod'
import {
  ChangeId,
  EndDate,
  Id,
  IsActive,
  IsActual,
  NextId,
  ObjectId,
  ObjectUuid,
  OperationTypeId,
  PrevId,
  StartDate,
  UpdateDate,
} from './common-schemas'
import { GarItem } from './gar-item'

export namespace Stead {
  export interface Data {
    /** @description Unique record identifier. Key field (ID) */
    readonly id: number

    /** @description Global unique identifier of the object (OBJECTID) */
    readonly objectId: number

    /** @description Global unique identifier of the object (OBJECTGUID) */
    readonly objectGuid: string

    /** @description Identifier of the changing transaction (CHANGEID) */
    readonly changeId: number

    /** @description Land plot number (NUMBER) */
    readonly number?: string

    /** @description Record action status (OPERTYPEID) */
    readonly operationTypeId: number

    /** @description Identifier of the record linking to the previous historical record (PREVID) */
    readonly prevId?: number

    /** @description Identifier of the record linking to the subsequent historical record (NEXTID) */
    readonly nextId?: number

    /** @description Record update date (UPDATEDATE) */
    readonly updateDate: Date

    /** @description Record start date (STARTDATE) */
    readonly startDate: Date

    /** @description Record end date (ENDDATE) */
    readonly endDate: Date

    /** @description Address object relevance indicator (ISACTUAL) */
    readonly isActual: boolean

    /** @description Active address object indicator (ISACTIVE) */
    readonly isActive: boolean
  }
}

/** @description Land plot information */
export class Stead extends GarItem<'Stead', Stead.Data> {
  static readonly SCHEMA: GarItem.Schema<Stead.Data> = z
    .strictObject({
      ID: Id,

      OBJECTID: ObjectId,

      OBJECTGUID: ObjectUuid,

      CHANGEID: ChangeId,

      NUMBER: z.string().describe('Land plot number').optional(),

      OPERTYPEID: OperationTypeId,

      PREVID: PrevId.optional(),

      NEXTID: NextId.optional(),

      UPDATEDATE: UpdateDate,

      STARTDATE: StartDate,

      ENDDATE: EndDate,

      ISACTUAL: IsActual,

      ISACTIVE: IsActive,
    })
    .transform<Stead.Data>(attributes => ({
      id: attributes.ID,
      objectId: attributes.OBJECTID,
      objectGuid: attributes.OBJECTGUID,
      changeId: attributes.CHANGEID,
      number: attributes.NUMBER,
      operationTypeId: attributes.OPERTYPEID,
      prevId: attributes.PREVID,
      nextId: attributes.NEXTID,
      updateDate: attributes.UPDATEDATE,
      startDate: attributes.STARTDATE,
      endDate: attributes.ENDDATE,
      isActual: attributes.ISACTUAL,
      isActive: attributes.ISACTIVE,
    }))

  readonly type = 'Stead'

  protected get schema() {
    return Stead.SCHEMA
  }
}
